import { StringInput } from 'components/StringInput';
import * as S from 'containers/PropertyPanel/PropertyPanel.styles';
import {
  CONVERSIONS,
  convertToCurrentMetric,
  getMetricUnit,
  roundToXDecimals,
} from 'util/conversions';
import { useEditFlowProperties } from '../useEditFlowProperties';
import { getMetricType } from 'store/settings/selectors';
import { useSelector } from 'react-redux';
import { NumberInput } from 'components/NumberInput';
import PropertyPanelAccordion from 'containers/PropertyPanel/components/PropertyPanelAccordion';
import PortDropdown from '../components/PortDropdown';
import { IconButton, Tooltip } from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { useTheme } from '@emotion/react';
import type { FlowPropertiesProps } from '../withFlowProperties';
import { withFlowProperties } from '../withFlowProperties';

type Props = FlowPropertiesProps & {
  showCurve: boolean;
  setShowCurve: (show: boolean) => void;
};

const MaterialProperties = ({
  id,
  activeUnit,
  flowProperties,
  showCurve,
  setShowCurve,
}: Props) => {
  const theme = useTheme();
  const metricType = useSelector(getMetricType);
  const { currentPort } = useEditFlowProperties(id, activeUnit, flowProperties);

  if (!currentPort?.outputFlow) {
    return null;
  }

  const grid =
    typeof currentPort?.outputFlow !== 'undefined'
      ? [['5', '1'], ['6'], ['3', '3'], ['3', '3'], ['3', '3']]
      : [['5', '1']];

  return (
    <PropertyPanelAccordion title='Material Properties' defaultExpanded>
      <S.GridLayout layout={grid}>
        <PortDropdown
          id={id}
          flowProperties={flowProperties}
          activeUnit={activeUnit}
        />
        <IconButton
          size='small'
          color='primary'
          sx={{ aspectRatio: '1' }}
          onClick={(e) => {
            e.stopPropagation();
            setShowCurve(!showCurve);
          }}
        >
          <Tooltip title={'Flow curve'}>
            <ShowChartIcon
              style={{
                fill: showCurve
                  ? theme.palette.primary.main
                  : theme.palette.grey['700'],
              }}
            />
          </Tooltip>
        </IconButton>
        {typeof currentPort?.outputFlow !== 'undefined' && (
          <>
            <StringInput
              value={`${convertToCurrentMetric({
                value: currentPort?.outputFlow?.lowerFractionSize,
                metricType,
                conversionType: CONVERSIONS.MILLIMETER,
              })} - ${convertToCurrentMetric({
                value: currentPort?.outputFlow?.upperFractionSize,
                metricType,
                conversionType: CONVERSIONS.MILLIMETER,
              })}`}
              setValue={() => {}}
              disabled
              label={`Size [${getMetricUnit(
                metricType,
                CONVERSIONS.MILLIMETER,
              )}]`}
            />
            <NumberInput
              value={currentPort.outputFlow?.totalMass}
              setValue={() => {}}
              disabled
              conversionType={CONVERSIONS.CAPACITY}
              label={`Flow rate [${getMetricUnit(
                metricType,
                CONVERSIONS.CAPACITY,
              )}]`}
              marginRight={16}
            />
            <NumberInput
              value={currentPort.outputFlow?.bulkDensity}
              conversionType={CONVERSIONS.DENSITY}
              setValue={() => {}}
              disabled
              label={`Bulk density [${getMetricUnit(
                metricType,
                CONVERSIONS.DENSITY,
              )}]`}
            />
            <NumberInput
              value={roundToXDecimals(currentPort.outputFlow?.workIndex, 1)}
              setValue={() => {}}
              disabled
              label={`Work index`}
              marginRight={16}
            />
            <NumberInput
              value={roundToXDecimals(currentPort.outputFlow?.abrasionIndex, 2)}
              setValue={() => {}}
              disabled
              label={`Abrasion index`}
            />
            <NumberInput
              value={roundToXDecimals(currentPort.outputFlow?.clay, 1)}
              setValue={() => {}}
              disabled
              label={`Clay [% by weight]`}
              marginRight={16}
            />
            <NumberInput
              value={roundToXDecimals(currentPort.outputFlow?.moisture, 1)}
              setValue={() => {}}
              disabled
              label={`Moisture [% by weight]`}
            />
          </>
        )}
      </S.GridLayout>
    </PropertyPanelAccordion>
  );
};

export default withFlowProperties(MaterialProperties);
